import { cn } from "@/lib/utils";

/**
 * Minimal inline SVG sparkline. Draws a polyline through the given numeric
 * points, scaled to a fixed viewBox so it embeds cleanly inside a KpiTile.
 * Optional baseline + endpoint dot help read the trend at a glance.
 *
 * Props:
 * - ``points`` — array of numbers (any unit; the chart auto-scales).
 * - ``tone`` — visual treatment. ``brand`` (default) for primary KPIs,
 *   ``positive`` / ``danger`` for tone-coded trends, ``muted`` for context.
 * - ``height`` — pixel height of the rendered chart (default 28).
 * - ``showEndDot`` — render a filled dot on the last point (default true).
 * - ``baseline`` — optional reference line (e.g. plan target) drawn as a
 *   dashed rule across the chart.
 */
export type SparklineTone = "brand" | "positive" | "danger" | "muted";

const TONE_STROKE: Record<SparklineTone, string> = {
  brand: "text-brand",
  positive: "text-success",
  danger: "text-danger",
  muted: "text-muted-foreground",
};

const TONE_FILL: Record<SparklineTone, string> = {
  brand: "fill-brand",
  positive: "fill-success",
  danger: "fill-danger",
  muted: "fill-muted-foreground",
};

export function Sparkline({
  points,
  tone = "brand",
  height = 28,
  showEndDot = true,
  baseline,
  className,
  ariaLabel,
}: {
  points: readonly number[];
  tone?: SparklineTone;
  height?: number;
  showEndDot?: boolean;
  baseline?: number;
  className?: string;
  ariaLabel?: string;
}) {
  if (!points.length) {
    return (
      <div
        className={cn("h-7 text-[10px] text-muted-foreground", className)}
        aria-label={ariaLabel ?? "Sparkline (no data)"}
      >
        —
      </div>
    );
  }

  const W = 100;
  const H = height;
  const PAD = 2;
  const max = Math.max(...points, baseline ?? -Infinity);
  const min = Math.min(...points, baseline ?? Infinity);
  const range = Math.max(max - min, 1);
  const xStep = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
  const yScale = (v: number) =>
    H - PAD - ((v - min) / range) * (H - PAD * 2);

  const d = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${(PAD + i * xStep).toFixed(2)},${yScale(p).toFixed(2)}`,
    )
    .join(" ");

  const last = points[points.length - 1] ?? 0;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={
        ariaLabel ??
        `Sparkline showing ${points.length} points, ending at ${last}`
      }
      className={cn("h-7 w-full", className)}
    >
      {baseline != null && (
        <line
          x1={PAD}
          x2={W - PAD}
          y1={yScale(baseline)}
          y2={yScale(baseline)}
          stroke="currentColor"
          strokeWidth={0.5}
          strokeDasharray="2 2"
          className="text-muted-foreground/60"
        />
      )}
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={TONE_STROKE[tone]}
      />
      {showEndDot && points.length > 0 && (
        <circle
          cx={PAD + (points.length - 1) * xStep}
          cy={yScale(last)}
          r={1.8}
          className={TONE_FILL[tone]}
        />
      )}
    </svg>
  );
}
